import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
  Badge, Box, Button, Chip, Divider, Drawer, Fab, IconButton, MenuItem,
  Stack, Tab, Tabs, TextField, Tooltip, Typography, useTheme,
} from "@mui/material";
import {
  ChecklistRtlOutlined, Close, DeleteOutline, NotificationsActiveOutlined,
  StickyNote2Outlined, AddTask,
} from "@mui/icons-material";
import {
  useGetPersonalOverviewQuery, useGetPersonalItemsQuery,
  useCreatePersonalItemMutation, useUpdatePersonalItemMutation,
  useDeletePersonalItemMutation,
} from "state/api";
import { canUsePersonalWorkspace } from "utils/roles";
import { useCrmContext } from "components/CrmContext";
import { useToast } from "components/Toast";
import useVirtualKeyboard from "hooks/useVirtualKeyboard";

// Floating personal workspace: to-dos, notes and reminders, scoped to the
// logged-in user. Deliberately NOT an assistant — it stores what you type and
// shows it back, nothing more.
//
// Mounted once in Layout, so it is present on every CRM view without any page
// knowing about it.

const KINDS = [
  { key: "task", label: "Task", Icon: ChecklistRtlOutlined },
  { key: "note", label: "Note", Icon: StickyNote2Outlined },
  { key: "reminder", label: "Reminder", Icon: NotificationsActiveOutlined },
];

const fmtDue = (d) => {
  if (!d) return null;
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return null;
  return dt.toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
};

// Quick "remind me in…" steps, in minutes. Most reminders in a working day are
// minutes away, and typing a full date for "in 20 minutes" is friction that
// stops people using it at all.
const QUICK_MINUTES = [15, 30, 45, 60, 120, 240];

const labelForMinutes = (m) =>
  m < 60 ? `${m}m` : m % 60 === 0 ? `${m / 60}h` : `${Math.floor(m / 60)}h${m % 60}`;

// A datetime-local value (what the custom picker wants) N minutes from now, in
// the user's OWN zone — toISOString would silently shift them to UTC.
const localInputFromNow = (minutes, now = new Date()) => {
  const t = new Date(now.getTime() + minutes * 60000);
  return new Date(t.getTime() - t.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};

const readableDue = (localValue) => {
  if (!localValue) return null;
  const d = new Date(localValue);
  if (Number.isNaN(d.getTime())) return null;
  const mins = Math.round((d.getTime() - Date.now()) / 60000);
  const when = d.toLocaleString(undefined, { hour: "2-digit", minute: "2-digit", month: "short", day: "numeric" });
  if (mins < 0) return `${when} (in the past)`;
  if (mins < 60) return `${when} · in ${mins}m`;
  if (mins < 1440) return `${when} · in ${Math.round(mins / 60)}h`;
  return when;
};

export default function PersonalWorkspace() {
  const theme = useTheme();
  const mb = theme.palette.extras?.mb || {};
  const user = useSelector((state) => state.auth.user);
  const { context } = useCrmContext();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  // Named rather than indexed: adding the To-do tab in front would otherwise
  // have silently renumbered every branch below.
  const [tab, setTab] = useState("todo");
  // Fixed bottom chrome hides while the on-screen keyboard is up (see the
  // hook and BottomNav for the iOS mid-screen bug this avoids).
  const keyboardOpen = useVirtualKeyboard();

  // Media Buying Head only — mirrored by requirePersonalWorkspace on the server,
  // which is the actual lock.
  const eligible = canUsePersonalWorkspace(user);

  const { data: overview } = useGetPersonalOverviewQuery(undefined, {
    skip: !eligible,
    pollingInterval: 120000,
    skipPollingIfUnfocused: true,
  });
  const { data: listData } = useGetPersonalItemsQuery(undefined, { skip: !eligible || !open });
  const [createItem, { isLoading: creating }] = useCreatePersonalItemMutation();
  const [updateItem] = useUpdatePersonalItemMutation();
  const [deleteItem] = useDeletePersonalItemMutation();

  const [draft, setDraft] = useState({ kind: "task", title: "", body: "", priority: "normal", dueAt: "", attach: true });
  const [customDue, setCustomDue] = useState(false);

  const badge = overview?.counts?.badge || 0;

  // A reminder whose time has come turns the whole button red. The server's
  // buckets are by calendar DAY (a "today" item stays "today" all day), so the
  // moment is judged here: any open item whose dueAt is now in the past. A
  // 30-second tick catches it without waiting for the two-minute poll.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(t);
  }, []);
  const dueNow = useMemo(() => {
    const all = [...(overview?.overdue || []), ...(overview?.today || []), ...(overview?.upcoming || [])];
    return all.some((it) => it.dueAt && it.status !== "done" && new Date(it.dueAt).getTime() <= now);
  }, [overview, now]);
  // Memoised: `listData?.items || []` builds a new array on every render, which
  // would make the notes memo below recompute every time and defeats its point.
  const items = useMemo(() => listData?.items || [], [listData]);
  const notes = useMemo(() => items.filter((i) => i.kind === "note"), [items]);
  // The to-do list is what was filed as a Task — dated or not. "Due" answers
  // "what's on the clock"; this answers "what did I say I'd do".
  const tasks = useMemo(() => items.filter((i) => i.kind === "task"), [items]);
  const openTasks = useMemo(() => tasks.filter((i) => i.status !== "done"), [tasks]);
  // Kept, but capped — a completed task is worth seeing for a moment, not
  // forever, and an unbounded list would push the open work off the screen.
  const doneTasks = useMemo(() => tasks.filter((i) => i.status === "done").slice(0, 10), [tasks]);

  if (!eligible) return null;

  const add = async () => {
    const title = draft.title.trim();
    if (!title) return;
    try {
      await createItem({
        kind: draft.kind,
        title,
        body: draft.body,
        priority: draft.priority,
        dueAt: draft.kind === "note" || !draft.dueAt ? null : new Date(draft.dueAt).toISOString(),
        // Filed against wherever the user was standing, if they want it.
        context: draft.attach && context ? context : null,
      }).unwrap();
      setDraft({ kind: draft.kind, title: "", body: "", priority: "normal", dueAt: "", attach: true });
      setCustomDue(false);
      toast.success(`${draft.kind === "note" ? "Note" : draft.kind === "reminder" ? "Reminder" : "Task"} saved.`);
    } catch (e) {
      toast.error(e?.data?.error || "Couldn't save that.");
    }
  };

  const toggleDone = async (it) => {
    try { await updateItem({ id: it._id, status: it.status === "done" ? "open" : "done" }).unwrap(); }
    catch { toast.error("Couldn't update that item."); }
  };

  const remove = async (it) => {
    try { await deleteItem(it._id).unwrap(); }
    catch { toast.error("Couldn't delete that item."); }
  };

  const Row = ({ it, tone, n }) => (
    <Stack direction="row" alignItems="flex-start" spacing={1} sx={{ py: 0.9 }}>
      {n != null && (
        <Typography sx={{
          fontSize: 11, fontWeight: 700, opacity: 0.45, minWidth: 16, textAlign: "right",
          mt: 0.5, fontVariantNumeric: "tabular-nums", flexShrink: 0,
        }}>
          {n}
        </Typography>
      )}
      <IconButton size="small" onClick={() => toggleDone(it)} sx={{ mt: -0.4 }}
        aria-label={it.status === "done" ? "Mark as not done" : "Mark as done"}>
        <Box sx={{
          width: 16, height: 16, borderRadius: "4px",
          border: `1.5px solid ${tone || mb.inkMid || "#8fa6a0"}`,
          bgcolor: it.status === "done" ? (mb.hold?.fg || "#7fb09a") : "transparent",
        }} />
      </IconButton>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{
          fontSize: 13.5, fontWeight: 600,
          textDecoration: it.status === "done" ? "line-through" : "none",
          opacity: it.status === "done" ? 0.55 : 1,
        }}>
          {it.title}
        </Typography>
        {it.body && <Typography variant="caption" sx={{ display: "block", opacity: 0.7 }}>{it.body}</Typography>}
        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.4, flexWrap: "wrap" }}>
          {it.dueAt && <Chip size="small" label={fmtDue(it.dueAt)} sx={{ height: 18, fontSize: 10, color: tone }} />}
          {it.priority === "high" && <Chip size="small" label="High" color="warning" sx={{ height: 18, fontSize: 10 }} />}
          {it.context?.label && (
            <Chip size="small" label={`${it.context.type}: ${it.context.label}`} sx={{ height: 18, fontSize: 10, opacity: 0.85 }} />
          )}
        </Stack>
      </Box>
      <IconButton size="small" onClick={() => remove(it)} aria-label="Delete">
        <DeleteOutline fontSize="small" sx={{ opacity: 0.5 }} />
      </IconButton>
    </Stack>
  );

  // Grouped by priority inside each section, and numbered straight down so the
  // list reads as an ordered plan rather than a pile. Numbering continues
  // across the priority groups — restarting at 1 in each would make "number 3"
  // ambiguous.
  const PRIORITY_ORDER = ["high", "normal", "low"];
  const Section = ({ title, rows, tone }) => {
    if (!rows?.length) return null;
    const groups = PRIORITY_ORDER
      .map((p) => [p, rows.filter((r) => (r.priority || "normal") === p)])
      .filter(([, list]) => list.length);
    let n = 0;
    return (
      <Box sx={{ mb: 1.5 }}>
        <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", opacity: 0.6, mb: 0.5 }}>
          {title} · {rows.length}
        </Typography>
        <Divider sx={{ mb: 0.5 }} />
        {groups.map(([priority, list]) => (
          <Box key={priority}>
            {/* Only labelled when there is more than one group — a lone
                "NORMAL" header over every task is noise. */}
            {groups.length > 1 && (
              <Typography sx={{
                fontSize: 9.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em",
                opacity: 0.45, mt: 0.75, mb: 0.25,
                color: priority === "high" ? (mb.status?.warn || "#f59e0b") : undefined,
              }}>
                {priority}
              </Typography>
            )}
            {list.map((it) => { n += 1; return <Row key={it._id} it={it} tone={tone} n={n} />; })}
          </Box>
        ))}
      </Box>
    );
  };

  return (
    <>
      <Tooltip title={dueNow ? "A reminder is due" : "My tasks & notes"}>
        <Fab
          size="medium"
          onClick={() => { setTab(dueNow ? "due" : "todo"); setOpen(true); }}
          data-due={dueNow ? "true" : undefined}
          sx={{
            position: "fixed",
            display: keyboardOpen ? "none" : undefined,
            // Clear of the mobile bottom nav and the safe area.
            bottom: { xs: "calc(70px + var(--safe-area-bottom, 0px))", sm: 24 },
            right: 20,
            zIndex: (t) => t.zIndex.speedDial,
            bgcolor: dueNow ? theme.palette.error.main : (mb.hold?.fg || "#7fb09a"),
            color: dueNow ? "#fff" : "#0f1f1c",
            "&:hover": { bgcolor: dueNow ? theme.palette.error.dark : "#6da88f" },
            // A slow pulse so a red button at the corner of the eye still reads
            // as "now", not as a colour the app always had.
            ...(dueNow ? {
              animation: "pw-due 1.6s ease-in-out infinite",
              "@keyframes pw-due": {
                "0%, 100%": { boxShadow: `0 0 0 0 ${theme.palette.error.main}88` },
                "50%": { boxShadow: `0 0 0 10px ${theme.palette.error.main}00` },
              },
            } : {}),
          }}
          aria-label={dueNow ? "My tasks and notes — a reminder is due" : "My tasks and notes"}
        >
          <Badge badgeContent={badge} color="error" max={99}>
            <AddTask />
          </Badge>
        </Fab>
      </Tooltip>

      <Drawer anchor="right" open={open} onClose={() => setOpen(false)}
        PaperProps={{ sx: { width: { xs: "100%", sm: 420 }, p: 2, bgcolor: mb.surface || undefined } }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em" }}>
            My workspace
          </Typography>
          <IconButton size="small" onClick={() => setOpen(false)} aria-label="Close"><Close fontSize="small" /></IconButton>
        </Stack>

        {/* Compose */}
        <Stack spacing={1} sx={{ mb: 1.5 }}>
          <Stack direction="row" spacing={0.75}>
            {KINDS.map(({ key, label, Icon }) => (
              <Button key={key} size="small" onClick={() => setDraft({ ...draft, kind: key })}
                variant={draft.kind === key ? "contained" : "outlined"}
                startIcon={<Icon sx={{ fontSize: 15 }} />}
                sx={{ fontSize: 11, flex: 1 }}>
                {label}
              </Button>
            ))}
          </Stack>
          <TextField size="small" placeholder={draft.kind === "note" ? "Write a note…" : "What needs doing?"}
            value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); add(); } }}
            fullWidth autoComplete="off" />
          {draft.kind !== "note" && (
            <>
              {/* Quick steps first — the common case is "in 20 minutes", and a
                  full date picker for that is why people stop bothering. */}
              <Stack direction="row" spacing={0.5} sx={{ flexWrap: "wrap", gap: 0.5 }}>
                {QUICK_MINUTES.map((m) => (
                  <Chip
                    key={m}
                    size="small"
                    label={labelForMinutes(m)}
                    onClick={() => { setDraft((d) => ({ ...d, dueAt: localInputFromNow(m) })); setCustomDue(false); }}
                    sx={{ fontSize: 11, cursor: "pointer" }}
                  />
                ))}
                <Chip size="small" label="Custom" variant={customDue ? "filled" : "outlined"}
                  onClick={() => setCustomDue((v) => !v)} sx={{ fontSize: 11, cursor: "pointer" }} />
                {draft.dueAt && (
                  <Chip size="small" label="Clear" variant="outlined"
                    onClick={() => { setDraft((d) => ({ ...d, dueAt: "" })); setCustomDue(false); }}
                    sx={{ fontSize: 11, cursor: "pointer", opacity: 0.7 }} />
                )}
              </Stack>
              {/* Always say what was actually chosen — a chip that just
                  highlights leaves you guessing what time you picked. */}
              {draft.dueAt && (
                <Typography variant="caption" sx={{ opacity: 0.75 }}>
                  Due {readableDue(draft.dueAt)}
                </Typography>
              )}
              <Stack direction="row" spacing={1}>
                {customDue && (
                  <TextField size="small" type="datetime-local" label="Due" InputLabelProps={{ shrink: true }}
                    value={draft.dueAt} onChange={(e) => setDraft({ ...draft, dueAt: e.target.value })} sx={{ flex: 1 }} />
                )}
                <TextField size="small" select label="Priority" value={draft.priority}
                  onChange={(e) => setDraft({ ...draft, priority: e.target.value })} sx={{ width: customDue ? 110 : "100%" }}>
                  {["low", "normal", "high"].map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                </TextField>
              </Stack>
            </>
          )}
          {/* Only offered when there IS something to attach to. */}
          {context && (
            <Button size="small" variant={draft.attach ? "contained" : "outlined"} color="inherit"
              onClick={() => setDraft({ ...draft, attach: !draft.attach })}
              sx={{ fontSize: 11, justifyContent: "flex-start", opacity: draft.attach ? 1 : 0.6 }}>
              {draft.attach ? "Filed under" : "Not filed"}: {context.label || context.ref} ({context.type})
            </Button>
          )}
          <Button size="small" variant="contained" onClick={add} disabled={creating || !draft.title.trim()}>
            Add
          </Button>
        </Stack>

        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons={false}
          sx={{ minHeight: 34, mb: 1 }}>
          <Tab value="todo" label="To-do" sx={{ minHeight: 34, fontSize: 12 }} />
          <Tab value="due" label="Due" sx={{ minHeight: 34, fontSize: 12 }} />
          <Tab value="notes" label="Notes" sx={{ minHeight: 34, fontSize: 12 }} />
          <Tab value="all" label="All" sx={{ minHeight: 34, fontSize: 12 }} />
        </Tabs>

        <Box sx={{ overflowY: "auto", flex: 1 }}>
          {tab === "todo" && (
            <>
              <Section title="To do" rows={openTasks} />
              {doneTasks.length > 0 && <Section title="Done" rows={doneTasks} />}
              {!tasks.length && (
                <Typography variant="body2" sx={{ opacity: 0.6, py: 2 }}>
                  No tasks yet. Add one above and it lands here.
                </Typography>
              )}
            </>
          )}
          {tab === "due" && (
            <>
              <Section title="Overdue" rows={overview?.overdue} tone={theme.palette.error.main} />
              <Section title="Today" rows={overview?.today} tone={mb.status?.warn || "#f59e0b"} />
              <Section title="Upcoming" rows={overview?.upcoming} />
              {!overview?.counts?.badge && !overview?.upcoming?.length && (
                <Typography variant="body2" sx={{ opacity: 0.6, py: 2 }}>Nothing due. </Typography>
              )}
            </>
          )}
          {tab === "notes" && (notes.length
            ? notes.map((it) => <Row key={it._id} it={it} />)
            : <Typography variant="body2" sx={{ opacity: 0.6, py: 2 }}>No notes yet.</Typography>)}
          {tab === "all" && (items.length
            ? <Section title="All" rows={items} />
            : <Typography variant="body2" sx={{ opacity: 0.6, py: 2 }}>Nothing saved yet.</Typography>)}
        </Box>
      </Drawer>
    </>
  );
}
